import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FIsTab } from './FIsTab';
import { PaymentIssuesCard } from './PaymentIssuesCard';
import './OverviewTab.css';

/**
 * Overview Tab Component
 * Displays platform-wide metrics organized by category with ISO table
 */
export function OverviewTab({ platformMetrics, fiMetrics, paymentIssues, fiLoading, onNavigateToTab }) {
  function formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  }

  function formatNumber(value) {
    return new Intl.NumberFormat('en-US').format(value || 0);
  }

  function handleMetricClick(tab) {
    if (onNavigateToTab) {
      onNavigateToTab(tab);
    }
  }

  if (!platformMetrics) {
    return (
      <Box p={3}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  // Calculate values for display
  const solventNetRevenue = parseFloat(platformMetrics.solvent_net_revenue || 0);

  // Settlement impact from rate variance (actual USD gain/loss from settlements)
  const rateVariance = platformMetrics.rate_variance || {};
  const settlementImpact = parseFloat(rateVariance.rate_variance_usd || 0);

  // Payment accuracy metrics
  const totalPaidTransactions = parseInt(platformMetrics.total_paid_transactions || platformMetrics.total_transactions || 0);
  const exactMatchCount = parseInt(platformMetrics.exact_match_count || 0);
  const paymentAccuracyPct = totalPaidTransactions > 0
    ? ((exactMatchCount / totalPaidTransactions) * 100).toFixed(1)
    : 0;

  // Compact Metric Card Component
  function CompactMetricCard({ icon, label, value, subtitle, color = 'primary', clickable, onClick }) {
    return (
      <div 
        className={`metric-card-compact ${color} ${clickable ? 'clickable' : ''}`}
        onClick={clickable ? onClick : undefined}
      >
        <div className="metric-icon">
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="metric-label">{label}</div>
        <div className="metric-value">{value}</div>
        {subtitle && <div className="metric-subtitle">{subtitle}</div>}
      </div>
    );
  }

  // Value Chip Component for gain/loss display
  function ValueChip({ value, showSign = true, invertColors = false }) {
    const isPositive = value >= 0;
    // invertColors: for costs, positive is bad (red), for revenue, positive is good (green)
    const chipClass = invertColors 
      ? (isPositive ? 'negative' : 'positive')
      : (isPositive ? 'positive' : 'negative');
    const displayValue = formatCurrency(Math.abs(value));
    const sign = showSign ? (isPositive ? '+' : '-') : '';
    
    return (
      <span className={`status-chip ${chipClass}`}>
        {sign}{displayValue.replace('$', '').replace('-', '')}
      </span>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Platform Overview
      </Typography>
      
      {/* Platform Activity Section */}
      <div className="metrics-section">
        <div className="metrics-section-title">Platform Activity</div>
        <div className="metrics-grid">
          <CompactMetricCard
            icon={['fal', 'receipt']}
            label="Total Transactions"
            value={formatNumber(platformMetrics.total_transactions)}
            subtitle="All time"
            color="primary"
            clickable
            onClick={() => handleMetricClick('transactions')}
          />
          <CompactMetricCard
            icon={['fal', 'dollar-sign']}
            label="Total Volume"
            value={formatCurrency(platformMetrics.total_volume_usd)}
            subtitle="USD processed"
            color="success"
            clickable
            onClick={() => handleMetricClick('transactions')}
          />
          <CompactMetricCard
            icon={['fal', 'store-alt']}
            label="Active ISOs"
            value={formatNumber(fiMetrics?.length || 0)}
            subtitle="ISOs"
            color="info"
          />
          <CompactMetricCard
            icon={['fal', 'chart-line']}
            label="Avg Transaction"
            value={formatCurrency(platformMetrics.avg_transaction_usd || 0)}
            subtitle="Per transaction"
            color="primary"
          />
        </div>
      </div>

      {/* Payment Issues Alert Card */}
      <PaymentIssuesCard
        paymentIssues={paymentIssues}
        onNavigateToTab={onNavigateToTab}
      />

      {/* Revenue & Profitability Section */}
      <div className="metrics-section">
        <div className="metrics-section-title">Solvent Revenue & Profitability</div>
        <div className="metrics-grid">
          <CompactMetricCard
            icon={['fal', 'comments-dollar']}
            label="Fees Revenue"
            value={formatCurrency(platformMetrics.solvent_fees_revenue || platformMetrics.solvent_gross_revenue || 0)}
            subtitle="From fee split (40% + buy rate)"
            color="success"
          />
          <CompactMetricCard
            icon={['fal', 'exchange-alt']}
            label="Settlement Impact"
            value={<ValueChip value={settlementImpact} />}
            subtitle="Rate variance from settlements"
            color={settlementImpact >= 0 ? 'success' : 'warning'}
          />
          <CompactMetricCard
            icon={['fal', 'money-check-alt']}
            label="Hard Costs"
            value={formatCurrency(platformMetrics.solvent_hard_costs || 0)}
            subtitle="Exchange + Bank fees"
            color="error"
          />
          <CompactMetricCard
            icon={['fal', 'piggy-bank']}
            label="Net Revenue"
            value={<ValueChip value={solventNetRevenue} />}
            subtitle="After costs & commissions"
            color={solventNetRevenue >= 0 ? 'success' : 'error'}
          />
        </div>
      </div>

      {/* Payment Accuracy Section */}
      <div className="metrics-section">
        <div className="metrics-section-title">Payment Accuracy</div>
        <div className="metrics-grid three-cards">
          <CompactMetricCard
            icon={['fal', 'bullseye-arrow']}
            label="Accuracy Rate"
            value={`${paymentAccuracyPct}%`}
            subtitle="Exact payment matches"
            color="success"
          />
          <CompactMetricCard
            icon={['fal', 'check-double']}
            label="Exact Matches"
            value={formatNumber(exactMatchCount)}
            subtitle={`of ${formatNumber(totalPaidTransactions)} paid`}
            color="info"
          />
          <CompactMetricCard
            icon={['fal', 'exclamation-triangle']}
            label="With Variance"
            value={formatNumber(totalPaidTransactions - exactMatchCount)}
            subtitle="Over or underpaid"
            color="warning"
          />
        </div>
      </div>

      {/* Commissions Section */}
      <div className="metrics-section">
        <div className="metrics-section-title">ISO Commissions</div>
        <div className="metrics-grid three-cards">
          <CompactMetricCard
            icon={['fal', 'coins']}
            label="Total Owed"
            value={formatCurrency(platformMetrics.total_commission_owed)}
            subtitle="Pending payouts"
            color="warning"
          />
          <CompactMetricCard
            icon={['fal', 'check-circle']}
            label="Total Paid"
            value={formatCurrency(platformMetrics.total_commission_paid)}
            subtitle="Completed payouts"
            color="success"
          />
          <CompactMetricCard
            icon={['fal', 'clock']}
            label="Outstanding"
            value={formatCurrency(
              (platformMetrics.total_commission_owed || 0) - (platformMetrics.total_commission_paid || 0)
            )}
            subtitle="Awaiting payment"
            color="error"
          />
        </div>
      </div>

      {/* ISOs Section */}
      <Box mt={4}>
        {fiLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <FIsTab fiMetrics={fiMetrics || []} />
        )}
      </Box>
    </Box>
  );
}
